import React from 'react';
import { useMutation } from '@apollo/client';
import TrackerUnlinkActivityMutation from './TrackerUnlinkActivityMutation';
import TrackerQuery from '../Fetch/TrackerQuery';

export type UnlinkActivityResult =
  | { ok: true }
  | { ok: false; reason: 'network' | 'server'; message: string };

interface ITrackerUnlinkActivityConnectorApolloProps {
  children: ({
    unlinkActivity,
  }: {
    unlinkActivity: (activityId: number) => Promise<UnlinkActivityResult>;
  }) => React.ReactElement;
  date: string;
}

const TrackerUnlinkActivityConnectorApollo: React.FC<
  ITrackerUnlinkActivityConnectorApolloProps
> = ({ children, date }) => {
  const [unlinkActivityMutation] = useMutation(TrackerUnlinkActivityMutation, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: TrackerQuery,
        variables: {
          date,
        },
      },
    ],
  });

  const unlinkActivity = async (activityId: number): Promise<UnlinkActivityResult> => {
    try {
      const response = await unlinkActivityMutation({ variables: { activityId } });
      const payload = response.data?.dailyTracker?.unlinkActivity;

      if (payload?.__typename === 'BasicMutationSuccess') {
        return { ok: true };
      }

      // Non-success payload (Error / UnauthorizedException / anything else).
      const message =
        payload?.description ||
        payload?.name ||
        payload?.errorCode ||
        payload?.authCode ||
        'unlink_server_error';
      return { ok: false, reason: 'server', message };
    } catch (error) {
      // Network error, GraphQL network-level failure, or thrown exception.
      const message = error instanceof Error ? error.message : 'unlink_network_error';
      return { ok: false, reason: 'network', message };
    }
  };

  return children({ unlinkActivity });
};

export default TrackerUnlinkActivityConnectorApollo;
